import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Upload, FileText, Loader2, CheckCircle2, Image, X, User } from "lucide-react";
import Navigation from "@/components/Navigation";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const ConsumptionUploadPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [file, setFile] = useState(null);
  const [clientName, setClientName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [reportType, setReportType] = useState('monosite'); // 'monosite' or 'multisites'

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile.name.endsWith('.pdf')) {
      toast.error('Format invalide', {
        description: 'Veuillez sélectionner un fichier PDF.',
      });
      return;
    }
    setFile(selectedFile);
  };

  const handleLogoSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Format invalide', {
        description: 'Veuillez sélectionner une image (PNG, JPG, GIF, SVG, WebP).',
      });
      return;
    }
    
    setLogoFile(selectedFile);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('report_type', reportType);
    if (clientName.trim()) {
      formData.append('client_name', clientName.trim());
    }

    try {
      const response = await axios.post(
        `${API}/api/consumption-reports/upload`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        const shareToken = response.data.report.share_token;
        const reportType = response.data.report.report_type;
        
        // Upload logo if provided
        if (logoFile) {
          try {
            const logoFormData = new FormData();
            logoFormData.append('file', logoFile);
            await axios.post(
              `${API}/api/consumption-reports/${shareToken}/logo`,
              logoFormData,
              {
                headers: {
                  ...getAuthHeader(),
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          } catch (logoError) {
            console.error('Logo upload error:', logoError);
            toast.warning('Rapport créé mais erreur lors de l\'upload du logo');
          }
        }
        
        toast.success('Rapport uploadé avec succès', {
          description: 'Le rapport de consommation a été analysé et enregistré.',
        });
        
        // Navigate to correct report view based on report type
        setTimeout(() => {
          if (reportType === 'consumption_multisites') {
            navigate(`/consumption/multisites/${shareToken}`);
          } else {
            navigate(`/consumption/report/${shareToken}`);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload', {
        description: error.response?.data?.detail || 'Une erreur est survenue lors de l\'upload du fichier.',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/10 to-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/consumption/reports')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux rapports
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Charger un rapport de consommation</CardTitle>
            <CardDescription>
              Uploadez un rapport PDF de consommation énergétique pour l'analyser et le transformer en format interactif
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Report Type Selector */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Type de rapport</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setReportType('monosite')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    reportType === 'monosite'
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <FileText className={`w-6 h-6 ${reportType === 'monosite' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="text-center">
                    <div className="font-medium">Monosite</div>
                    <div className="text-xs text-muted-foreground">Un seul bâtiment</div>
                  </div>
                  {reportType === 'monosite' && (
                    <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-primary" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setReportType('multisites')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    reportType === 'multisites'
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="relative">
                    <FileText className={`w-6 h-6 ${reportType === 'multisites' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <FileText className={`w-4 h-4 absolute -right-1 -top-1 ${reportType === 'multisites' ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="text-center">
                    <div className="font-medium">Multisites</div>
                    <div className="text-xs text-muted-foreground">Plusieurs bâtiments</div>
                  </div>
                  {reportType === 'multisites' && (
                    <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-primary" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {reportType === 'monosite' 
                  ? 'Sélectionnez ce type pour un rapport concernant un seul bâtiment/PDL'
                  : 'Sélectionnez ce type pour un rapport consolidé avec plusieurs sites/bâtiments'}
              </p>
            </div>

            {/* Client Name Input */}
            <div className="space-y-2">
              <Label htmlFor="client_name" className="text-sm font-medium flex items-center gap-2">
                <User className="w-4 h-4" />
                Nom du client
              </Label>
              <Input
                id="client_name"
                placeholder="Ex: LOGEA, Carrefour Market, etc."
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                disabled={uploading}
                className="max-w-md"
              />
              <p className="text-xs text-muted-foreground">
                Ce nom apparaîtra dans la liste des rapports pour faciliter la recherche
              </p>
            </div>

            {/* Client Logo Upload (Optional) */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Logo du client (optionnel)</label>
              <p className="text-xs text-muted-foreground mb-2">
                Ajoutez le logo de votre client pour personnaliser le rapport et l'export PDF
              </p>
              
              {logoPreview ? (
                <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/30">
                  <img 
                    src={logoPreview} 
                    alt="Logo preview" 
                    className="h-16 w-auto max-w-[200px] object-contain"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{logoFile?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {logoFile && (logoFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removeLogo}
                    disabled={uploading}
                    className="shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                    onChange={handleLogoSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={uploading}
                    id="logo-input"
                  />
                  <div className="flex items-center gap-3 p-4 border-2 border-dashed rounded-lg hover:border-primary/50 hover:bg-accent/30 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                      <Image className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Cliquez pour ajouter un logo</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG, GIF, SVG, WebP</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Zone */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-accent/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileInput}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              
              {file ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    disabled={uploading}
                  >
                    Changer de fichier
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto bg-accent rounded-full flex items-center justify-center">
                    <Upload className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold mb-2">
                      Glissez-déposez votre fichier PDF ici
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ou cliquez pour parcourir vos fichiers
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-semibold mb-2">Format accepté :</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Fichier PDF uniquement</li>
                <li>Rapport de consommation avec données mensuelles</li>
                <li>Taille maximale : 10 MB</li>
              </ul>
            </div>

            {/* Action Button */}
            {file && (
              <Button
                onClick={handleUpload}
                disabled={uploading}
                size="lg"
                className="w-full"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Analyser et enregistrer
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConsumptionUploadPage;
